import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watchEffect } from "vue";
import { storeToRefs } from "pinia";
import { useEUAccountsStore } from '@/store_v3/EUAccountsStore';
import { CellsType, RawConfig, prepareItems } from "@/utils/CellsFactory";
import { ITableHeader, showServerError } from "@/utils";
import { getUnpaidInvoices } from "@/api/revenue";
import { IEUAccountNettings, INettingItem } from "@/api/types/revenue";
import { getCurrencyIcon } from "@/utils/translate";
import { getFormatDate } from "@/utils/formatDates";
import BaseRadio from "@/components-UI/BaseRadio.vue";
import BaseTable from "@/components-UI/table/BaseTable.vue";
import BaseList from "@/components-UI/list/BaseList.vue";
import BaseListItem from "@/components-UI/list/BaseListItem.vue";
import BaseButton from "@/components-UI/BaseButton.vue";
import SvgPlus from "@/assets/icons/plus.svg";
import SvgCheck from "@/assets/icons/check.svg";
import SvgTrash from "@/assets/icons/trash.svg";
import BaseInputNumber from "@/components-UI/BaseInputNumber.vue";
import BaseEditableCard from "@/components-UI/BaseEditableCard.vue";
import router from "@/router";


export default /*#__PURE__*/_defineComponent({
  __name: 'ComponentNettings',
  setup(__props) {

const EUAccountStore = useEUAccountsStore();
const {
    isLoading,
    form,
    currentRadioValue,
    getNettingsTotalSum,
    isEdit,
} = storeToRefs(EUAccountStore);
const {
    formatNumber,
    recountNettingsSum,
    fetchRate,
} = EUAccountStore;

const radios: { value: string | number, labelAfter: string }[] = [
    { value: '1', labelAfter: 'Без взаимозачёта' },
    { value: '2', labelAfter: 'По акту' },
    { value: '3', labelAfter: 'По счёту вебмастера' },
];
const tableItemsTop = ref<Array<INettingItem & { checked: boolean }>>([]);
const tableOffset = ref(0);
const tableHeadersTop: ITableHeader[] = [
    { text: 'ID счёта', value: 'affiliate_invoice_id', width: '144px', align: 'center' },
    { text: 'ID партнёра', value: 'affiliate_id', width: '144px', align: 'center' },
    { text: 'Сумма счёта', value: 'amount', width: 'auto', align: 'end' },
    { text: 'Остаток на счёте', value: 'balance', width: 'auto', align: 'end' },
    { text: '', value: 'actions', width: '48px', align: 'center' },
];
const tableConfigsTop: RawConfig<INettingItem & { checked: boolean, balance: number, actions: any }> = {
    [CellsType.Route]: {
        affiliate_invoice_id: {
            name: 'affiliate-invoice',
            isChip: true,
            valueKey: 'affiliate_invoice_id',
        },
        affiliate_id: {
            name: 'affiliate',
            isChip: true,
            valueKey: 'affiliate_id',
        },
    },
    [CellsType.Number]: {
        amount: {
            currency: (i: INettingItem) => i.currency!,
            isSymbolCurrency: true,
        },
        balance: {
            value: (i: INettingItem) => formatNumber(i.amount! - i.partials_amount!),
            currency: (i: INettingItem) => i.currency!,
            isSymbolCurrency: true,
        },
    },
    [CellsType.Uniq]: {
        actions: {},
    },
};
const getTableTopFormatItems = computed(() =>
    prepareItems(
        tableItemsTop.value,
        tableConfigsTop,
        tableHeadersTop.map(h => h.value),
    ),
);
const tableHeadersBottom: ITableHeader[] = [
    { text: 'ID счёта', value: 'affiliate_invoice_id', width: '144px', align: 'center' },
    { text: 'ID партнёра', value: 'affiliate_id', width: '144px', align: 'center' },
    { text: 'Сумма счёта', value: 'amount', width: 'auto', align: 'end' },
    { text: 'Остаток на счёте', value: 'balance', width: 'auto', align: 'end' },
    { text: 'Курс', value: 'exchange_rate', width: 'auto', align: 'end' },
    { text: 'Итоговая сумма', value: 'original_amount', width: 'auto', align: 'end' },
    { text: 'Сумма оплаты', value: 'final_amount', width: 'auto', align: 'end' },
    { text: '', value: 'actions', width: '44px', align: 'center' },
];
const tableConfigsBottom: RawConfig<IEUAccountNettings & { actions: any }> = {
    [CellsType.Route]: {
        affiliate_invoice_id: {
            name: 'affiliate-invoice',
            isChip: true,
            valueKey: 'affiliate_invoice_id',
        },
        affiliate_id: {
            name: 'affiliate',
            isChip: true,
            valueKey: 'affiliate_id',
        },
    },
    [CellsType.Number]: {
        amount: {
            currency: (i: IEUAccountNettings) => i.currency!,
            isSymbolCurrency: true,
        },
        balance: {
            currency: (i: IEUAccountNettings) => i.currency!,
            isSymbolCurrency: true,
        },
        original_amount: {
            currency: () => form.value.currency,
            isSymbolCurrency: true,
        },
    },
    [CellsType.Uniq]: {
        exchange_rate: {},
        final_amount: {},
        actions: {},
    },
};
const getTableBottomFormatItems = computed(() =>
    prepareItems(
        form.value.nettings,
        tableConfigsBottom,
        tableHeadersBottom.map(h => h.value),
    ),
);

const getHeaderTableForSinglePage = computed(() => {
    const label = radios.find(el => el.value === currentRadioValue.value)?.labelAfter;
    return label === 'По акту' ? `${label}:` : label;
});

const isSingle = computed(() => router.currentRoute.params.id !== 'create');

const handleClickRadio = async (event: string): Promise<void> => {
    currentRadioValue.value = event;
    form.value.netting_act_amount = null;
    form.value.nettings = [];
    tableOffset.value = 0;

    if (event === '3' && !form.value.nettings?.length) {
        await fetchUnpaidInvoices();
    }
};

const addItemInTableBottom = async (item: IEUAccountNettings): Promise<void> => {
    const customItem: IEUAccountNettings = JSON.parse(JSON.stringify({
        ...item,
        exchange_rate: 0,
        original_amount: 0,
        final_amount: 0,
        balance: 0,
    }));

    if (form.value.nettings?.some(el => el.affiliate_invoice_id === customItem.affiliate_invoice_id)) {
        item.checked = true;
        return;
    }

    item.checked = true;

    if (form.value.currency !== customItem.currency) {
        const { operation, original_rate } = await fetchRate({
            from: customItem.currency,
            to: form.value.currency,
            date: getFormatDate(customItem.created_at!, 'yyyy-MM-dd'),
        });

        customItem.operation = operation;
        customItem.exchange_rate = original_rate;
    }

    customItem.final_amount = item.original_amount;
    form.value.nettings = [customItem, ...form.value.nettings];

    recountNettingsSum(customItem);
};

const removeItemFromBottomTable = (item: IEUAccountNettings): void => {
    form.value.nettings = form.value.nettings?.filter(el => el.affiliate_invoice_id !== item.affiliate_invoice_id);

    const selectedItem = tableItemsTop.value.find(el => el.affiliate_invoice_id === item.affiliate_invoice_id);

    if (selectedItem) {
        selectedItem.checked = false;
    }
};

const fetchUnpaidInvoices = async (): Promise<void> => {
    try {
        if (!form.value.advertiser_id) return;

        isLoading.value.offsetting = true;

        const resp: { data: INettingItem[] } = await getUnpaidInvoices(
            form.value.advertiser_id,
            { offset: 0, limit: 100 },
        );

        tableItemsTop.value = resp.data.map(el => ({
            ...el,
            ...{ checked: false },
        }));
    } catch (e) {
        showServerError(e, 'Список для взаимозачета не загружен');
    } finally {
        isLoading.value.offsetting = false;
    }
};

watchEffect(async () => {
    if (form.value.advertiser_id && isSingle.value) {
        if (form.value.nettings?.length) {
            currentRadioValue.value = '3';

            if (isEdit.value && !tableItemsTop.value.length) {
                await fetchUnpaidInvoices();

                form.value.nettings?.forEach((el: IEUAccountNettings) => {
                    const tableItem: INettingItem & { checked: true } = tableItemsTop.value.find((e: INettingItem) =>
                        e.affiliate_invoice_id === el.affiliate_invoice_id,
                    );

                    if (tableItem) {
                        tableItem.checked = true;
                    }
                });
            }

            if (!isEdit.value && form.value.nettings.length) {
                const filteredNettings = JSON.parse(JSON.stringify(form.value.nettings.filter((el: IEUAccountNettings) =>
                    form.value?.nettings?.some((e: IEUAccountNettings) =>
                        e.affiliate_invoice_id === el.affiliate_invoice_id,
                    ),
                )));
                
                const remainingInvoiceIds = new Set(filteredNettings.map((el: IEUAccountNettings) => el.affiliate_invoice_id));
                
                tableItemsTop.value.forEach((item) => {
                    if (!remainingInvoiceIds.has(item.affiliate_invoice_id)) {
                        item.checked = false;
                    }
                });
                
                form.value.nettings = filteredNettings;
            }
        }

        if (form.value.netting_act_amount) {
            currentRadioValue.value = '2';
        }
    }
});

return { __sfc: true,EUAccountStore, isLoading, form, currentRadioValue, getNettingsTotalSum, isEdit, formatNumber, recountNettingsSum, fetchRate, radios, tableItemsTop, tableOffset, tableHeadersTop, tableConfigsTop, getTableTopFormatItems, tableHeadersBottom, tableConfigsBottom, getTableBottomFormatItems, getHeaderTableForSinglePage, isSingle, handleClickRadio, addItemInTableBottom, removeItemFromBottomTable, fetchUnpaidInvoices, getCurrencyIcon, BaseRadio, BaseTable, BaseList, BaseListItem, BaseButton, SvgPlus, SvgCheck, SvgTrash, BaseInputNumber, BaseEditableCard }
}

})